import { action, makeAutoObservable } from 'mobx';
import { noop } from 'lodash';
import {
  updateJobPosting,
  createJobPosting,
  fetchJobPostings,
  fetchJobPostingById,
  updateJobPostingStatus,
  deleteJobPosting
} from '../api/SlmService';
import notificationStore from './NotificationStore';
import jobPostingNotificationsStore from './JobPostingNotificationsStore';
import { jobPostingValidators } from '../utils/validations';
import { JOB_POSTING_STATUS } from '../utils/constants';
import { createCopiedTitle } from '../utils/formatters';

class JobPostingStore {
  jobPostingDialogOpen = false;

  isJobPostingLoading = false;

  isJobPostingFetching = false;

  activeJobPosting = {};

  jobPostings = [];

  jobPostingsPaginationInfo = {};

  pendingCancellationJobPostingId = null;

  pendingCompletionJobPostingId = null;

  pendingReopenJobPostingId = null;

  pendingReopenJobPostingDeadlineToApply = null;

  pendingDeletionJobPosting = null;

  pendingSaveAsDraftPosting = null;

  pendingPublishingJobPosting = null;

  page = null;

  sort = {
    by: null,
    order: null
  };

  copyUrlNotificationVisible = false;

  notificationStore = {};

  constructor(newNotificationStore, newJobPostingNotificationsStore) {
    this.notificationStore = newNotificationStore;
    this.jobPostingNotificationsStore = newJobPostingNotificationsStore;
    makeAutoObservable(this);
  }

  setSort = (next) => {
    this.sort = { ...next };
  };

  setPage = (p) => {
    this.page = p;
  };

  setJobPostings = (list) => {
    this.jobPostings = list;
  };

  setJobPostingsPaginationInfo = (paginationInfo) => {
    this.jobPostingsPaginationInfo = paginationInfo;
  };

  setIsJobPostingLoading = (bool) => {
    this.isJobPostingLoading = bool;
  };

  setIsJobPostingFetching = (bool) => {
    this.isJobPostingFetching = bool;
  };

  setCopyUrlNotificationVisible = (bool) => {
    this.copyUrlNotificationVisible = bool;
  };

  /**
   * Control Job Posting Dialog open state
   * @param {Boolean} value
   */
  toggleEditJobPostingModal = (value = false) => {
    this.jobPostingDialogOpen = value;
  };

  setActiveJobPosting = (data = {}) => {
    this.activeJobPosting = data;
  };

  /**
   * sets id for job posting cancellation modal
   * dont pass an argument to close the modal
   * @param {*} id job posting id
   */
  toggleCancelJobPostingModal = (jobPostingId) => {
    this.pendingCancellationJobPostingId = jobPostingId;
  };

  /**
   * sets id for job posting completion modal
   * dont pass an argument to close the modal
   * @param {*} id job posting id
   */
  toggleMarkCompleteModal = (id) => {
    this.pendingCompletionJobPostingId = id;
  };

  /**
   * sets id for job posting reopen modal
   * dont pass an argument to close the modal
   * @param {*} id job posting id
   */
  toggleReopenJobPostingModal = (id, deadlineToApply) => {
    this.pendingReopenJobPostingId = id;
    this.pendingReopenJobPostingDeadlineToApply = deadlineToApply;
  };

  /**
   * sets object for job posting deletion modal
   * dont pass an argument to close the modal
   * @param {*} jobPosting job posting object
   */
  toggleDeleteJobPostingModal = (jobPosting) => {
    this.pendingDeletionJobPosting = jobPosting;
  };

  /**
   * sets object for job posting deletion modal
   * dont pass an argument to close the modal
   * @param {*} jobPosting job posting object
   */
  toggleUnsavedWorkModal = (jobPosting) => {
    this.pendingSaveAsDraftPosting = jobPosting;
  };

  /**
   * sets object for job posting publishing modal
   * dont pass an argument to close the modal
   * @param {*} jobPosting job posting object
   */
   togglePublishJobPostingModal = (jobPosting) => {
     this.pendingPublishingJobPosting = jobPosting;
   };

  fetchJobPostings = (withLoader = true) => {
    if (withLoader) {
      this.setIsJobPostingLoading(true);
    }
    fetchJobPostings(this.page, this.sort)
      .then(
        action('success', (res) => {
          this.setJobPostingsPaginationInfo(res.metadata.paginationInfo);
          this.setJobPostings(res.jobPostingDisplays);
        }),
        action('error', () => {
          this.notificationStore.setError('Failed to retrieve job postings.');
        })
      )
      .finally(() => {
        this.setIsJobPostingLoading(false);
      });
  };

  /**
   * Save job posting data. If data has id - job posting is edit, otherwise creates new job posting
   * @param {Object} jobPosting job posting data
   * @param {Function} toggleDialogCb dialog or modal toggle callback
   */
  saveJobPosting = (jobPosting, toggleDialogCb = noop) => {
    this.setIsJobPostingLoading(true);
    if (jobPosting.jobPostingId) {
      updateJobPosting(jobPosting).then(
        action('success', () => {
          if (window.location.pathname?.includes(jobPosting.jobPostingId)) {
            this.getJobPosting(jobPosting.jobPostingId);
          }
          this.fetchJobPostings();
        }),
        action('error', () => {
          this.notificationStore.setError('Failed to update existing job posting.');
        })
      ).finally(() => {
        toggleDialogCb();
        this.jobPostingNotificationsStore.showJobPostingUpdatedNotification();
      });
    } else {
      createJobPosting(jobPosting).then(
        action('success', (res) => {
          toggleDialogCb(res.jobPostingId);
        }),
        action('error', () => {
          this.notificationStore.setError('Failed to create new job posting.');
          this.fetchJobPostings();
        })
      ).finally(() => {
        toggleDialogCb();
      });
    }
  };

  copyJobPosting = async (jobPostingId, callback) => {

    const jobPosting = await fetchJobPostingById(jobPostingId);

    const newJobPosting = {
      ...jobPosting,
      title: createCopiedTitle(jobPosting.title),
      status: JOB_POSTING_STATUS.DRAFT
    };

    delete newJobPosting.jobPostingId;

    this.setIsJobPostingLoading(true);

    createJobPosting(newJobPosting).then(
      action('success', (res) => {
        callback(res.jobPostingId);
        this.setActiveJobPosting(res);
        this.fetchJobPostings(false);
      }),
      action('error', () => {
        this.notificationStore.setError('Failed to copy job posting.');
      })
    ).finally(() => {
      this.setIsJobPostingLoading(false);
    });
  };

  getJobPosting = async (jobPostingId) => {
    this.setIsJobPostingFetching(true);
    const data = await fetchJobPostingById(jobPostingId);
    this.setActiveJobPosting(data);
    this.setIsJobPostingFetching(false);
    return data;
  };

  /**
   * Prepare Job Posting Dialog to edit existing job posting
   * @param {String} id job posting id
   */
  editJobPosting = async (jobPostingId) => {
    this.toggleEditJobPostingModal(true);
    this.getJobPosting(jobPostingId);
  };

  /**
   * Updates job posting status
   * @param {*} jobPostingId
   * @param {*} status
   * @param {*} toggleDialogCb used for closing confirmation dialogs
   */
  updateJobPostingStatus = (jobPostingId, status, jobPostingPage, toggleDialogCb = noop, reOpen, callback) => {
    this.setIsJobPostingLoading(true);
    updateJobPostingStatus(jobPostingId, status).then(
      action('success', () => {
        if (jobPostingPage) {
          this.getJobPosting(jobPostingId);
        } else {
          if (reOpen) {
            this.getJobPosting(jobPostingId);
            callback(jobPostingId);
          }
          this.fetchJobPostings(true);
        }
      }),
      action('error', () => {
        this.notificationStore.setError('Failed to update job posting status.');
      })
    ).finally(() => {
      this.setIsJobPostingLoading(false);
      toggleDialogCb();
    });
  };

  deleteJobPosting = (jobPostingId) => {
    this.setIsJobPostingLoading(true);
    deleteJobPosting(jobPostingId).then(
      action('success', () => {
        this.fetchJobPostings();
      }),
      action('error', () => {
        this.notificationStore.setError('Failed to delete job posting.');
      })
    ).finally(() => {
      this.setIsJobPostingLoading(false);
      this.toggleDeleteJobPostingModal();
    });
  };

  showCopyUrlNotificationVisible = () => {
    this.setCopyUrlNotificationVisible(true);
    setTimeout(() => {
      this.setCopyUrlNotificationVisible(false);
    }, 5000);
  };

  canPublishJobPosting = () => {
    return !Object.values(jobPostingValidators).every((it) => it(this.activeJobPosting));
  };
}

export default new JobPostingStore(notificationStore, jobPostingNotificationsStore);
